/**
 * 表示ユーティリティ
 * 依頼データの表示に関する共通処理を提供する
 */

import { Irai, iraiHeader } from "../types/GxpTypes";

/**
 * 詳細モーダルで除外するフィールド
 * createdAt, updatedAt はAPIレスポンスに含まれるが型定義には含まれていない
 */
export const EXCLUDED_DETAIL_FIELDS: string[] = [
  "id",
  "statusId",
  "slideOriginNum",
  "procCnt",
  "approvalDate",
  "dataApprovalDoctorId",
  "approvalDoctorId",
  "formNo",
  "organizationId",
  "organizationName",
  "anonymousId",
  "patientFirstName",
  "patientLastName",
  "specimenId",
  "createdAt",
  "updatedAt",
  "diagnosisId",
];

/**
 * レビュー画面で除外するフィールド
 * createdAt, updatedAt はAPIレスポンスに含まれるが型定義には含まれていない
 */
export const EXCLUDED_REVIEW_FIELDS: string[] = [
  "id",
  "statusId",
  "status",
  "slideOriginNum",
  "procCnt",
  "iraiDate",
  "approvalDate",
  "dataApprovalDoctorId",
  "dataApprovalDoctorName",
  "approvalDoctorId",
  "formNo",
  "organizationId",
  "organizationName",
  "anonymousId",
  "patientFirstName",
  "patientLastName",
  "specimenId",
  "updatedAt",
  "createdAt",
];

type IraiEntry = [string, string | number | Date | null];

/**
 * 依頼データから表示用のエントリを取得（詳細モーダル用）
 * @param irai - 依頼データ
 * @returns 表示用エントリ配列
 */
export const getDisplayEntriesForDetail = (irai: Irai): IraiEntry[] => {
  return Object.entries(irai)
    .filter(([key, value]) => {
      const isExcludedKey = EXCLUDED_DETAIL_FIELDS.includes(key);
      const hasValidValue =
        value !== null &&
        value !== undefined &&
        value !== "" &&
        String(value).trim() !== "";
      return !isExcludedKey && hasValidValue;
    })
    .sort(([keyA], [keyB]) => sortByIraiHeader(keyA, keyB)) as IraiEntry[];
};

/**
 * 依頼データから表示用のエントリを取得（レビュー画面用）
 * @param irai - 依頼データ
 * @returns 表示用エントリ配列
 */
export const getDisplayEntriesForReview = (irai: Irai): IraiEntry[] => {
  return Object.entries(irai)
    .filter(([key]) => !EXCLUDED_REVIEW_FIELDS.includes(key)) as IraiEntry[];
};

/**
 * iraiHeaderの順番に基づいてソート
 * @param keyA - 比較キーA
 * @param keyB - 比較キーB
 * @returns ソート順序
 */
export const sortByIraiHeader = (keyA: string, keyB: string): number => {
  const headerKeys = Object.keys(iraiHeader);
  const indexA = headerKeys.indexOf(keyA);
  const indexB = headerKeys.indexOf(keyB);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }
  if (indexA !== -1) {
    return -1;
  }
  if (indexB !== -1) {
    return 1;
  }
  return 0;
};

/**
 * エントリを2列に分割
 * @param entries - エントリ配列
 * @returns [左列, 右列]
 */
export const splitEntriesForColumns = (
  entries: IraiEntry[]
): [IraiEntry[], IraiEntry[]] => {
  const midIndex = Math.ceil(entries.length / 2);
  return [entries.slice(0, midIndex), entries.slice(midIndex)];
};
